import React, { useCallback, useEffect, useMemo, useState } from "react";
import OrderTable from "../../components/order/OrderTable";
import { SortType } from "../../../models/SortType";
import {
  Box,
  CircularProgress,
  Container,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import CustomTextField from "../../components/common/textFields/TextField";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../utils/NotificationsContext";
import FilterSidebar from "../../components/common/filter/FilterSidebar";
import { FilterSidebarMenu, FilterMenuType } from "../../../models/FilterMenu";
import { ModuleType } from "../../../models/ModuleType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import dayjs from "dayjs";
import { OrdersFilters, OrderTypeFilter } from "../../../models/Order/Order";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { OrderType } from "../../../models/Project/OrderType";

const Orders: React.FC = () => {
  const { orderStore, userStore } = useStores();
  const { showError } = useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [selectedShipmentIdsMap, setSelectedShipmentIdsMap] = useState<
    Map<number, number>
  >(new Map<number, number>());
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [canAddOrder, setCanAddOrder] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setCanAddOrder(userStore.hasPermission(EmployeeRoles.CREATE_ORDER));
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseIdValuePairs = (param: string): FilterValue[] =>
    param.split(",").map((pair) => {
      const [id, value] = pair.split(":");
      return { id: id || "", value: value || "" };
    });

  const initialFilters: OrdersFilters = {
    page_no: searchParams.get("page_no")
      ? parseInt(searchParams.get("page_no") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    limit: parseInt(searchParams.get("limit") || "30", 10),
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    customer_id: [],
    user_id: [],
    admin_id: [],
    order_id: [],
    order_type: [OrderTypeFilter[0]],
    start_date: undefined,
    end_date: undefined,
    order_types: ["1", "2", "3", "4", "5", "6"], // Default to all order types
  };

  const initialParams: OrdersFilters = {
    page_no: parseInt(searchParams.get("page_no") || "1", 10),
    search_text: searchParams.get("search_text") || undefined,
    limit: parseInt(searchParams.get("limit") || "30", 10),
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,

    customer_id: searchParams.get("customer_id")
      ? parseIdValuePairs(searchParams.get("customer_id")!)
      : [],
    user_id: searchParams.get("user_id")
      ? parseIdValuePairs(searchParams.get("user_id")!)
      : [],
    admin_id: searchParams.get("admin_id")
      ? parseIdValuePairs(searchParams.get("admin_id")!)
      : [],
    order_id: searchParams.get("order_id")
      ? parseIdValuePairs(searchParams.get("order_id")!)
      : [],
    order_type: searchParams.get("order_type")
      ? parseIdValuePairs(searchParams.get("order_type")!)
      : [OrderTypeFilter[0]],
    start_date: searchParams.get("start_date")
      ? new Date(searchParams.get("start_date")!)
      : undefined,
    end_date: searchParams.get("end_date")
      ? new Date(searchParams.get("end_date")!)
      : undefined,
    order_types: searchParams.get("order_types")
      ? searchParams.get("order_types")!.split(",")
      : ["1", "2", "3", "4", "5", "6"], // Default to all order types
  };

  const [filters, setFilters] = useState<OrdersFilters>(initialParams);
  const [params, setParams] = useState<OrdersFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );

  const handleFilterChange = useCallback(
    (key: keyof OrdersFilters, value: OrdersFilters[keyof OrdersFilters]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const filterMenu: FilterSidebarMenu[] = useMemo(() => {
    return [
      {
        moduleType: ModuleType.CUSTOMER,
        fieldType: FilterMenuType.MULTI_SELECT,
        subType: "search_customer",
        label: "Customer",
        placeholder: "Enter Customer name",
        value: filters.customer_id,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("customer_id", value),
      },
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_admin",
        label: "Admin",
        placeholder: "Enter Admin name",
        value: filters.admin_id,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("admin_id", value),
      },
      {
        moduleType: ModuleType.USER,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_user",
        label: "User",
        placeholder: "Enter User name",
        value: filters.user_id,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("user_id", value),
      },
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_order",
        label: "Order",
        placeholder: "Search Order",
        value: filters.order_id,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("order_id", value),
      },
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.LOCAL_MULTI_SELECT,
        subType: "order",
        label: "Order Types",
        list: OrderType,
        value: filters?.order_types?.map((item) => item) || [],
        onChange: (selectedValues: string[]) => {
          handleFilterChange("order_types", selectedValues);
        },
      },
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.BOOLEAN,
        subType: "order",
        label: "Order Status",
        list: OrderTypeFilter,
        value: filters.order_type,
        onChange: (selectedValues: FilterValue[]) =>
          handleFilterChange("order_type", selectedValues),
      },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleApplyFilters = () => {
    setParams(filters);
    handleFilterToggle();
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setParams(initialFilters);
    handleFilterToggle();
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (
            value.length > 0 &&
            typeof value[0] === "object" &&
            "id" in value[0] &&
            "value" in value[0]
          ) {
            queryParams.append(
              key,
              value
                .map((item: FilterValue) => `${item.id}:${item.value}`)
                .join(","),
            );
          }
        } else if (key === "start_date" && value?.selectedDate) {
          const date = dayjs(value.selectedDate);
          if (date.isValid()) {
            queryParams.append("start_date", date.toISOString());
          }
        } else if (key === "end_date" && value?.selectedDate) {
          const date = dayjs(value.selectedDate);
          if (date.isValid()) {
            queryParams.append("end_date", date.toISOString());
          }
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        navigate({ search: `?${queryString}` }, { replace: true });
      }
    };
    updateQueryParams();
  }, [params, navigate]);

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_ORDER)) {
      setIsLoading(true);
      orderStore
        .getOrderListing(params)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = (query: string) => {
    if (query.trim().length > 0) {
      setSearchTerm(query);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: undefined,
        page_no: 1,
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleFilterToggle = () => {
    setOpenFilter(!openFilter);
  };

  return (
    <>
      {isLoading ? (
        <Grid2
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          sx={{
            minHeight: "80vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Grid2>
      ) : (
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <Paper>
            {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
              <Box
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                  bgcolor: "#FFFFFF",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  paddingX: 3,
                  paddingY: 2,
                  minHeight: { xs: "60vh", md: window.innerHeight - 110 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <CustomTypography variant="heading3" fontWeight={700}>
                    Orders
                  </CustomTypography>
                  {canAddOrder && (
                    <AppButton
                      startIcon={<AddOutlinedIcon />}
                      variant="primary"
                      label="Add Order"
                      onClick={() => {
                        orderStore.reset();
                        navigate("/orders/add-orders");
                      }}
                      customStyles={{ width: "140px" }}
                    />
                  )}
                </Box>

                <Grid2
                  container
                  spacing={2}
                  columns={12}
                  sx={{
                    mt: 3,
                    mb: 3,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Grid2
                    size={{ xs: 12, sm: 12, md: 11, lg: 11 }}
                    sx={{ minWidth: 0 }}
                  >
                    <CustomTextField
                      placeholder={"Search by order id or customer name"}
                      value={searchTerm}
                      onSearch={(query) => handleSearch(query)}
                      onKeyDown={handleKeyDown}
                      width={"100%"}
                    />
                  </Grid2>
                  <Grid2
                    size={{ xs: 12, sm: 12, md: 1, lg: 1 }}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                    }}
                  >
                    <AppButton
                      variant="grayscale"
                      label="Filter"
                      startIcon={<FilterAltOutlinedIcon />}
                      onClick={() => {
                        handleFilterToggle();
                      }}
                      customStyles={{ width: "125px", flexShrink: 0 }}
                    />
                  </Grid2>
                </Grid2>

                {isLoading && (
                  <Container
                    sx={{
                      minHeight: "60vh",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress />
                  </Container>
                )}

                {!isLoading && orderStore.orders.length <= 0 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="10rem"
                    textAlign="center"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      No data found
                    </Typography>
                  </Box>
                )}

                {!isLoading && orderStore.orders.length > 0 && (
                  <OrderTable
                    maxHeight={window.innerHeight - 340}
                    limit={params.limit || orderStore.limit}
                    page_no={params.page_no}
                    total_count={orderStore.total_count}
                    total_pages={orderStore.total_pages}
                    sortBy={params.sort_by}
                    sortType={params.sort_type}
                    data={orderStore.orders}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    selectedShipmentIdsMap={selectedShipmentIdsMap}
                    onSortChange={(column, sortType) => {
                      setParams((prev) => ({
                        ...prev,
                        sort_by: column,
                        sort_type: sortType,
                      }));
                    }}
                    selectAllItem={() => {
                      const map = new Map<number, number>();
                      orderStore.orders.forEach((item) =>
                        map.set(item.id, item.id),
                      );
                      setSelectedShipmentIdsMap(map);
                    }}
                    deSelectAllItem={() => {
                      setSelectedShipmentIdsMap(new Map());
                    }}
                    selectItem={(item) => {
                      const map = new Map(selectedShipmentIdsMap);
                      map.set(item.id, item.id);
                      setSelectedShipmentIdsMap(map);
                    }}
                    deSelectItem={(item) => {
                      const map = new Map(selectedShipmentIdsMap);
                      map.delete(item.id);
                      setSelectedShipmentIdsMap(map);
                    }}
                  />
                )}

                {openFilter && (
                  <FilterSidebar
                    isOpen={openFilter}
                    items={filterMenu}
                    onApply={handleApplyFilters}
                    handleClear={handleClearFilters}
                    toggleFilter={handleFilterToggle}
                  />
                )}
              </Box>
            ) : (
              <PermissionBlockLabel />
            )}
          </Paper>
        </Grid2>
      )}
    </>
  );
};
export default React.memo(Orders);
